import { Button } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import FactDrawer from '@/cam-admin/control-panel/FactDrawer';
import { type FactGroup } from '@/karehero-app/tell-us-once/intake/FactList';
import {
  type FormItem,
  type FormQuestion,
} from '@/cam-admin/control-panel/fixtures';

/**
 * A form's answers as facts.
 *
 * A section is a group and a question is a row, which is what they already
 * were: the form detail drew its own review with its own Edit, Save and Cancel
 * on every row and its own answered counts per section. That was a third way
 * of editing a record in one workspace, and the review is the one we have
 * decided on.
 *
 * The answer is carried as its label rather than its stored value, because a
 * row shows what it holds and 'yes' is not what the form says. Writing it back
 * turns the label into the value again. See FormDrawer's onSave.
 */
const formFacts = (form: FormItem): FactGroup[] =>
  form.sections.map((section) => ({
    label: section.title,
    // Every question, including the conditional ones. A care expert may be
    // filling the form in on somebody's behalf, and a question they cannot see
    // is one they cannot answer. The sections fold instead, which is how a
    // fourteen-section form stays navigable.
    rows: section.questions.map(factRow),
  }));

/** One question, as a row. */
const factRow = (q: FormQuestion) => ({
  id: q.id,
  label: q.question,
  value: answerLabel(q),
  // Says which ones only apply sometimes, now they are all on screen together.
  // The row says so as a chip; the schema's sentence is longer than the fact.
  ...(q.conditional ? { conditional: true } : {}),
  ...control(q),
});

/** What the row opens as. A question's kind is how the form takes its answer. */
const control = (q: FormQuestion) => {
  if (q.kind === 'date') return { control: 'date' as const };
  if (q.options) {
    return {
      control: (q.kind === 'multi' ? 'multi' : 'choice') as 'multi' | 'choice',
      choices: q.options.map((o) => o.label),
    };
  }
  return {};
};

/** What the row shows: an option's label, a currency with its sign, else the answer. */
const answerLabel = (q: FormQuestion): string => {
  const v = q.answer.trim();
  if (!v) return '';
  if (q.kind === 'currency') return `£${v}`;
  return q.options?.find((o) => o.value === v)?.label ?? v;
};

/** Back the other way, for what gets stored. */
const answerValue = (q: FormQuestion, label: string): string => {
  if (q.kind === 'currency') return label.replace(/^£/, '').trim();
  return q.options?.find((o) => o.label === label)?.value ?? label;
};

/**
 * A form, opened over the workspace rather than swallowing the tab.
 *
 * It used to replace the Forms list with a page of its own, with a back link
 * to get out of. A form is a thing you look at while you are working on a
 * circle, not somewhere you go, so it opens the way a carer and the recipient
 * do.
 */
const FormDrawer = ({
  open,
  form,
  onSaveAnswer,
  onDownload,
  onClose,
}: {
  open: boolean;
  form?: FormItem;
  onSaveAnswer: (questionId: string, value: string) => void;
  /**
   * Put the answers on the real form.
   *
   * Production calls this Download Form: the API fills the government PDF from
   * the care profile and hands it back as a file (front-end/pwa/src/pdf/
   * attendanceAllowance.ts). Reviewing the answers here and then retyping them
   * onto the form is the work this is meant to remove, so the drawer that holds
   * them is where the button belongs.
   */
  onDownload?: () => void;
  onClose: () => void;
}) => (
  <FactDrawer
    open={open}
    title={form?.name ?? ''}
    subtitle={form?.description}
    groups={form ? formFacts(form) : []}
    // Fourteen sections and 227 questions: the section names are how a care
    // expert gets around it, and they work one section at a time.
    collapsible
    // And the whole frame if they want it: at 440px a question and its answer
    // stack, so the same form is several screens longer than it has to be.
    expandable
    footer={
      onDownload && (
        <Button
          variant='contained'
          color='primary'
          startIcon={<Icon icon='file-lines' fontSize={16} />}
          onClick={onDownload}
        >
          Download form
        </Button>
      )
    }
    onSave={(id, label) => {
      const question = form?.sections
        .flatMap((s) => s.questions)
        .find((q) => q.id === id);
      if (question) onSaveAnswer(id, answerValue(question, label));
    }}
    onClose={onClose}
  />
);

export default FormDrawer;
